import { env } from '@/shared/config/env';
import { endpoints } from './endpoints';
import { getSessionToken } from './session-token';

type SSEEventHandler = (event: MessageEvent) => void;

/**
 * Server-Sent Events client for real-time notifications.
 * Manages a single EventSource connection with automatic reconnection.
 */
class SSEClient {
  private eventSource: EventSource | null = null;
  private handlers: Map<string, Set<SSEEventHandler>> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private organizationId: string | null = null;

  /**
   * Opens an SSE connection to the notifications stream.
   */
  connect(organizationId: string): void {
    if (this.eventSource) {
      return;
    }

    this.organizationId = organizationId;
    const token = getSessionToken();
    const baseUrl = `${env.VITE_API_BASE_URL}${endpoints.notifications.sseStream(organizationId)}`;
    const url = token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl;
    this.eventSource = new EventSource(url, { withCredentials: true });

    this.eventSource.onopen = () => {
      this.reconnectAttempts = 0;
    };

    this.eventSource.onerror = () => {
      this.eventSource?.close();
      this.eventSource = null;

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
        setTimeout(() => {
          if (this.organizationId) {
            this.connect(this.organizationId);
          }
        }, delay);
      }
    };

    this.eventSource.onmessage = (event) => {
      const messageHandlers = this.handlers.get('message');
      messageHandlers?.forEach((handler) => handler(event));
    };
  }

  /**
   * Subscribes to SSE events of a specific type.
   */
  on(eventType: string, handler: SSEEventHandler): () => void {
    if (!this.handlers.has(eventType)) {
      this.handlers.set(eventType, new Set());

      // Register named event listener on EventSource if connected
      if (this.eventSource && eventType !== 'message') {
        this.eventSource.addEventListener(eventType, handler);
      }
    }

    this.handlers.get(eventType)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(eventType)?.delete(handler);
    };
  }

  /**
   * Closes the SSE connection and clears all handlers.
   */
  disconnect(): void {
    this.eventSource?.close();
    this.eventSource = null;
    this.handlers.clear();
    this.reconnectAttempts = 0;
    this.organizationId = null;
  }
}

/**
 * Singleton SSE client instance.
 */
export const sseClient = new SSEClient();
